import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    ChoiceChips,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Text,
    Textarea,
} from "@athena/ui";
import { FollowUpPicker } from "./FollowUpPicker";
import type { ContactChannel, FollowUpChoice } from "../helpers/client";
import { CONTACT_CHANNELS, FOLLOW_UP_DATE_CHOICES, resolveFollowUp } from "../helpers/client";

export interface ScheduledFollowUp {
    date: Date;
    channel: ContactChannel;
    note?: string;
}

interface ScheduleFollowUpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientName: string;
    today: Date;
    /** Saves it. The dialog closes once it has; a failed save keeps it open with what was entered. */
    onSchedule: (followUp: ScheduledFollowUp) => Promise<void>;
}

/** Scheduling is always for some date, so unlike logging a contact there is no "none". */
const FOLLOW_UP_CHOICES: FollowUpChoice[] = [...FOLLOW_UP_DATE_CHOICES];

export function ScheduleFollowUpDialog({
    open,
    onOpenChange,
    clientName,
    today,
    onSchedule,
}: ScheduleFollowUpDialogProps) {
    const { t } = useTranslation();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t("admin.alumni.followUp.title")}</DialogTitle>
                    <DialogDescription>
                        {t("admin.alumni.followUp.description", { name: clientName })}
                    </DialogDescription>
                </DialogHeader>
                {/* Its own component so its state resets each time the dialog opens — Radix unmounts the content on close. */}
                <ScheduleFollowUpForm
                    clientName={clientName}
                    today={today}
                    onCancel={() => onOpenChange(false)}
                    onSubmit={async (followUp) => {
                        await onSchedule(followUp);
                        onOpenChange(false);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function ScheduleFollowUpForm({
    clientName,
    today,
    onCancel,
    onSubmit,
}: {
    clientName: string;
    today: Date;
    onCancel: () => void;
    onSubmit: (followUp: ScheduledFollowUp) => Promise<void>;
}) {
    const { t } = useTranslation();
    const ids = useId();
    const [
        choice,
        setChoice,
    ] = useState<FollowUpChoice>("oneWeek");
    const [
        customDate,
        setCustomDate,
    ] = useState<Date | undefined>();
    const [
        channel,
        setChannel,
    ] = useState<ContactChannel>("call");
    const [
        note,
        setNote,
    ] = useState<string>("");
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);

    const date = resolveFollowUp(choice, customDate, today);

    const submit = async () => {
        if (!date) {
            return;
        }
        setSaving(true);
        try {
            await onSubmit({ date, channel, note: note.trim() || undefined });
        } catch {
            // Already said so; stay open with what was entered.
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <Text id={`${ids}-when`} size="sm" weight="semibold">
                    {t("admin.alumni.followUp.whenLabel")}
                </Text>
                <FollowUpPicker
                    labelledBy={`${ids}-when`}
                    choices={FOLLOW_UP_CHOICES}
                    choice={choice}
                    onChoiceChange={setChoice}
                    customDate={customDate}
                    onCustomDateChange={setCustomDate}
                    today={today}
                />
            </section>

            <section className="flex flex-col gap-2">
                <Text id={`${ids}-channel`} size="sm" weight="semibold">
                    {t("admin.alumni.followUp.channelLabel")}
                </Text>
                <ChoiceChips
                    aria-labelledby={`${ids}-channel`}
                    value={channel}
                    onValueChange={(value) => setChannel(value as ContactChannel)}
                    options={CONTACT_CHANNELS.map((value) => ({ value, label: t(`admin.alumni.channel.${value}`) }))}
                />
            </section>

            <section className="flex flex-col gap-2">
                <Text as="div" size="sm" weight="semibold">
                    <label htmlFor={`${ids}-note`}>{t("admin.alumni.followUp.noteLabel")}</label>
                </Text>
                <Textarea
                    id={`${ids}-note`}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder={t("admin.alumni.followUp.notePlaceholder", { name: clientName })}
                />
            </section>

            <DialogFooter>
                <Button variant="ghost" onClick={onCancel}>
                    {t("common.actions.cancel")}
                </Button>
                <Button variant="secondary" disabled={!date || saving} onClick={() => void submit()}>
                    {t("admin.alumni.followUp.submit")}
                </Button>
            </DialogFooter>
        </div>
    );
}
